import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heading, Subheading } from "../../../design/heading";
import { Button } from "../../../design/button";
import { Text } from "../../../design/text";
import { Description, Field, FieldGroup, Label } from "../../../design/fieldset";
import { Input } from "../../../design/input";
import { Select } from "../../../design/select";
import { useBusiness } from "../../../contexts/BusinessContext";
import { useAccount } from "../../../contexts/AccountContext";
import { Card } from "../../../design/card";
import { BuildingLibraryIcon, CheckIcon } from "@heroicons/react/20/solid";
import {
  OrganizationType,
  NonprofitStatus,
  YearsInOperationRange,
  MonthlyCasesRange,
  ServiceFocus,
  OrganizationProfile,
} from "@visitproof/types";

// Label mappings for display
const ORGANIZATION_TYPE_LABELS: Record<OrganizationType, string> = {
  for_profit: "For-Profit Business",
  nonprofit: "Nonprofit Organization",
  government: "Government Agency",
  educational: "Educational Institution",
  religious: "Religious Organization",
  individual: "Individual/Sole Proprietor",
  other: "Other",
};

const NONPROFIT_STATUS_LABELS: Record<NonprofitStatus, string> = {
  "501c3": "501(c)(3) - Charitable",
  "501c4": "501(c)(4) - Social Welfare",
  other: "Other Nonprofit Status",
  pending: "Pending Determination",
};

const YEARS_IN_OPERATION_LABELS: Record<YearsInOperationRange, string> = {
  less_than_1: "Less than 1 year",
  "1_to_3": "1-3 years",
  "3_to_5": "3-5 years",
  "5_to_10": "5-10 years",
  "10_plus": "10+ years",
};

const MONTHLY_CASES_LABELS: Record<MonthlyCasesRange, string> = {
  "1_to_10": "1-10 cases",
  "11_to_25": "11-25 cases",
  "26_to_50": "26-50 cases",
  "51_to_100": "51-100 cases",
  "100_plus": "100+ cases",
};

const SERVICE_FOCUS_OPTIONS: { value: ServiceFocus; label: string }[] = [
  { value: "supervised_visitation", label: "Supervised Visitation" },
  { value: "therapeutic_visitation", label: "Therapeutic Visitation" },
  { value: "exchanges_only", label: "Exchanges Only" },
  { value: "reunification_therapy", label: "Reunification Therapy" },
  { value: "monitoring_only", label: "Monitoring Only" },
  { value: "parent_education", label: "Parent Education" },
  { value: "other", label: "Other Services" },
];

const OrganizationStep: React.FC = () => {
  const navigate = useNavigate();
  const { account } = useAccount();
  const { business, updateBusiness } = useBusiness();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form state from existing business data
  const [data, setData] = useState<OrganizationProfile>({
    legalName: business?.organizationProfile?.legalName || "",
    organizationType: business?.organizationProfile?.organizationType || undefined,
    nonprofitDetails: business?.organizationProfile?.nonprofitDetails || {
      status: "501c3",
      verificationStatus: "unverified",
    },
    yearsInOperation: business?.organizationProfile?.yearsInOperation || undefined,
    averageMonthlyCases: business?.organizationProfile?.averageMonthlyCases || undefined,
    serviceFocus: business?.organizationProfile?.serviceFocus || [],
    receivesGovernmentFunding: business?.organizationProfile?.receivesGovernmentFunding || false,
  });

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      // Build the update payload
      const updatePayload: { organizationProfile: OrganizationProfile } = {
        organizationProfile: {
          ...data,
          // Only include nonprofitDetails if organizationType is nonprofit
          nonprofitDetails:
            data.organizationType === "nonprofit" ? data.nonprofitDetails : undefined,
        },
      };

      await updateBusiness(account.id, updatePayload);
      navigate("/onboarding/calendar");
    } catch (e) {
      setError("Failed to save organization information. Please try again.");
      console.error("Error saving organization info:", e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate("/onboarding/address");
  };

  const handleSkip = () => {
    navigate("/onboarding/calendar");
  };

  const toggleServiceFocus = (service: ServiceFocus) => {
    const currentServices = data.serviceFocus || [];
    if (currentServices.includes(service)) {
      setData({
        ...data,
        serviceFocus: currentServices.filter((s: ServiceFocus) => s !== service),
      });
    } else {
      setData({
        ...data,
        serviceFocus: [...currentServices, service],
      });
    }
  };

  const isNonprofit = data.organizationType === "nonprofit";

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="space-y-2 text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
            <BuildingLibraryIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
        </div>
        <Heading>About Your Organization</Heading>
        <Text className="text-lg text-gray-600 dark:text-gray-400">
          Help us understand your organization better
        </Text>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <span className="flex-shrink-0 text-xl">ℹ️</span>
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <Text>
              This information helps us tailor the platform to your needs and may qualify you for
              special programs. All fields are optional.
            </Text>
          </div>
        </div>
      </Card>

      {error && (
        <div className="p-3 border border-red-200 rounded-lg bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <Text className="text-sm text-red-700 dark:text-red-300">{error}</Text>
        </div>
      )}

      {/* Form */}
      <Card>
        <form onSubmit={handleSubmit}>
          <FieldGroup className="space-y-6">
            {/* Legal Name */}
            <Field>
              <Label>Legal Organization Name</Label>
              <Description>
                The official name of your organization (may differ from your DBA/trade name)
              </Description>
              <Input
                placeholder="e.g., ABC Family Services, Inc."
                value={data.legalName || ""}
                onChange={(e) => setData({ ...data, legalName: e.target.value })}
              />
            </Field>

            {/* Organization Type */}
            <Field>
              <Label>Organization Type</Label>
              <Select
                value={data.organizationType || ""}
                onChange={(e) =>
                  setData({
                    ...data,
                    organizationType: (e.target.value as OrganizationType) || undefined,
                  })
                }
              >
                <option value="">Select organization type...</option>
                {Object.entries(ORGANIZATION_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>

            {/* Nonprofit Details - Only shown when nonprofit is selected */}
            {isNonprofit && (
              <div className="space-y-4 p-4 bg-gray-50 dark:bg-zinc-800/50 rounded-lg border border-gray-200 dark:border-zinc-700">
                <Subheading>Nonprofit Details</Subheading>

                <Field>
                  <Label>IRS Designation</Label>
                  <Select
                    value={data.nonprofitDetails?.status || "501c3"}
                    onChange={(e) =>
                      setData({
                        ...data,
                        nonprofitDetails: {
                          ...data.nonprofitDetails,
                          status: e.target.value as NonprofitStatus,
                          verificationStatus:
                            data.nonprofitDetails?.verificationStatus || "unverified",
                        },
                      })
                    }
                  >
                    {Object.entries(NONPROFIT_STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field>
                  <Label>EIN (Employer Identification Number)</Label>
                  <Description>Format: XX-XXXXXXX (optional)</Description>
                  <Input
                    placeholder="12-3456789"
                    value={data.nonprofitDetails?.ein || ""}
                    onChange={(e) =>
                      setData({
                        ...data,
                        nonprofitDetails: {
                          ...data.nonprofitDetails,
                          status: data.nonprofitDetails?.status || "501c3",
                          verificationStatus:
                            data.nonprofitDetails?.verificationStatus || "unverified",
                          ein: e.target.value,
                        },
                      })
                    }
                  />
                </Field>

                <Field>
                  <Label>State of Registration</Label>
                  <Input
                    placeholder="e.g., California"
                    value={data.nonprofitDetails?.stateOfRegistration || ""}
                    onChange={(e) =>
                      setData({
                        ...data,
                        nonprofitDetails: {
                          ...data.nonprofitDetails,
                          status: data.nonprofitDetails?.status || "501c3",
                          verificationStatus:
                            data.nonprofitDetails?.verificationStatus || "unverified",
                          stateOfRegistration: e.target.value,
                        },
                      })
                    }
                  />
                </Field>
              </div>
            )}

            {/* Years in Operation */}
            <Field>
              <Label>Years in Operation</Label>
              <Select
                value={data.yearsInOperation || ""}
                onChange={(e) =>
                  setData({
                    ...data,
                    yearsInOperation: (e.target.value as YearsInOperationRange) || undefined,
                  })
                }
              >
                <option value="">Select...</option>
                {Object.entries(YEARS_IN_OPERATION_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>

            {/* Average Monthly Cases */}
            <Field>
              <Label>Average Monthly Cases</Label>
              <Description>How many cases do you typically handle per month?</Description>
              <Select
                value={data.averageMonthlyCases || ""}
                onChange={(e) =>
                  setData({
                    ...data,
                    averageMonthlyCases: (e.target.value as MonthlyCasesRange) || undefined,
                  })
                }
              >
                <option value="">Select...</option>
                {Object.entries(MONTHLY_CASES_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>

            {/* Services Provided */}
            <Field>
              <Label>Services Provided</Label>
              <Description>Select all that apply</Description>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {SERVICE_FOCUS_OPTIONS.map(({ value, label }) => {
                  const isSelected = (data.serviceFocus || []).includes(value);
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => toggleServiceFocus(value)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${
                        isSelected
                          ? "bg-blue-100 text-blue-700 border-2 border-blue-500 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-600"
                          : "bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200 dark:bg-zinc-800 dark:text-gray-300 dark:hover:bg-zinc-700"
                      }`}
                    >
                      {isSelected && <CheckIcon className="h-4 w-4 flex-shrink-0" />}
                      <span className={isSelected ? "" : "ml-6"}>{label}</span>
                    </button>
                  );
                })}
              </div>
            </Field>

            {/* Government Funding */}
            <Field>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="governmentFunding"
                  checked={data.receivesGovernmentFunding || false}
                  onChange={(e) =>
                    setData({ ...data, receivesGovernmentFunding: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-zinc-600 dark:bg-zinc-800"
                />
                <Label htmlFor="governmentFunding" className="!mb-0 cursor-pointer">
                  We receive government funding or contracts
                </Label>
              </div>
            </Field>
          </FieldGroup>
        </form>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-between gap-3">
        <Button outline className="flex-1 py-3 text-base" onClick={handleBack}>
          Back
        </Button>
        <Button outline className="py-3 text-base" onClick={handleSkip}>
          Skip
        </Button>
        <Button
          color="blue"
          className="flex-1 py-3 text-base font-semibold"
          onClick={() => handleSubmit()}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Continue"}
        </Button>
      </div>
    </div>
  );
};

export default OrganizationStep;
